import moment, { type Moment } from "moment";
import type { TFunction } from "i18next";
import type { Client } from "#/types/client";
import { DEFAULT_DATE_FORMAT, DEFAULT_TIME_FORMAT } from "#/config/dateTimeFormats";

/**
 * Centralized date/time formatting utilities.
 *
 * These functions should be used throughout the application instead of
 * calling moment.format() directly. This ensures all date/time displays
 * respect the client's configured format preferences.
 */

/**
 * Get the date format for a client, or default
 */
export const getDateFormat = (client: Client | null | undefined): string => {
  return client?.dateFormat || DEFAULT_DATE_FORMAT;
};

/**
 * Get the time format for a client, or default
 */
export const getTimeFormat = (client: Client | null | undefined): string => {
  return client?.timeFormat || DEFAULT_TIME_FORMAT;
};

/**
 * Format a date according to client preferences
 *
 * @param date - Date to format (can be string, Date, or Moment)
 * @param client - Client object with format preferences
 * @returns Formatted date string
 */
export const formatDate = (
  date: string | Date | Moment | null | undefined,
  client: Client | null | undefined
): string => {
  if (!date) return "--";

  const dateFormat = getDateFormat(client);
  return moment.utc(date).local().format(dateFormat);
};

/**
 * Format a time according to client preferences
 *
 * @param date - Date to format (can be string, Date, or Moment)
 * @param client - Client object with format preferences
 * @returns Formatted time string
 */
export const formatTime = (
  date: string | Date | Moment | null | undefined,
  client: Client | null | undefined
): string => {
  if (!date) return "--";

  const timeFormat = getTimeFormat(client);
  return moment.utc(date).local().format(timeFormat);
};

/**
 * Format a date and time according to client preferences
 *
 * @param date - Date to format (can be string, Date, or Moment)
 * @param client - Client object with format preferences
 * @returns Formatted date and time string
 */
export const formatDateTime = (
  date: string | Date | Moment | null | undefined,
  client: Client | null | undefined
): string => {
  if (!date) return "--";

  const dateFormat = getDateFormat(client);
  const timeFormat = getTimeFormat(client);
  return moment.utc(date).local().format(`${dateFormat} ${timeFormat}`);
};

/**
 * Whole calendar days between the given date and today (local time).
 *
 * Uses start-of-day on both sides so "today" is 0, yesterday is 1, etc.
 * Returns null for empty input and clamps future dates (clock skew) to 0.
 *
 * @param date - Date to measure from
 * @returns Number of days ago, or null when no date is provided
 */
export const daysSince = (date: string | Date | Moment | null | undefined): number | null => {
  if (!date) return null;

  const then = moment.utc(date).local().startOf("day");
  const today = moment().startOf("day");
  const diff = today.diff(then, "days");
  return diff < 0 ? 0 : diff;
};

/**
 * Format a date and time for export (CSV, etc.)
 * Always uses UTC and a standard format
 *
 * @param date - Date to format
 * @returns Formatted date/time string for export
 */
export const formatDateTimeForExport = (
  date: string | Date | Moment | null | undefined
): string => {
  if (!date) return "";
  return moment.utc(date).format("DD/MM/YYYY HH:mm");
};

/**
 * Format an item's "Last Moved" value for CSV export as the relative day
 * count plus the absolute timestamp, e.g. "5 days ago (17/06/2026 08:07)".
 * Returns an empty string when no date is provided.
 *
 * @param date - The last-moved date
 * @param t - i18next translate function (for the "days ago" copy)
 * @returns Combined day-count + timestamp string
 */
export const formatLastMovedForExport = (
  date: string | Date | Moment | null | undefined,
  t: TFunction
): string => {
  if (!date) return "";
  const days = daysSince(date) ?? 0;
  return `${t("common.daysAgo", { count: days })} (${formatDateTimeForExport(date)})`;
};

/**
 * The weekday name of a date in the UI language ("Monday", "Montag"), for
 * places where a dispatcher plans by day of the week. Empty for no date.
 *
 * @param style - "long" (default) for the full name, "short" for "Mon" / "Mo."
 */
export const formatWeekday = (
  date: string | Date | Moment | null | undefined,
  language: string,
  style: "long" | "short" = "long"
): string => {
  if (!date) return "";
  return new Intl.DateTimeFormat(language, { weekday: style }).format(
    moment.utc(date).local().toDate()
  );
};

/** ISO weekday numbers, Monday first, as stored on drivers' working days. */
export const ISO_WEEKDAYS = [1, 2, 3, 4, 5, 6, 7] as const;

/**
 * Short weekday name ("Mon", "Mo.") of an ISO weekday number (1 = Monday …
 * 7 = Sunday) in the UI language, for pickers that are not about a date.
 */
export const weekdayShortName = (isoWeekday: number, language: string): string => {
  // 2024-01-01 is a Monday; adding (isoWeekday − 1) days lands on the wanted weekday.
  const reference = new Date(Date.UTC(2024, 0, isoWeekday, 12));
  return new Intl.DateTimeFormat(language, { weekday: "short", timeZone: "UTC" }).format(reference);
};

/**
 * Local calendar day of a stored UTC date as "YYYY-MM-DD", the key to group
 * records by the day the user sees them on. Null for no date.
 */
export const localDayKey = (date: string | Date | Moment | null | undefined): string | null => {
  if (!date) return null;
  return moment.utc(date).local().format("YYYY-MM-DD");
};

/**
 * Whether two dates fall on the same local calendar day. False when either is
 * missing, so a missing date never matches anything.
 */
export const isSameLocalDay = (
  a: string | Date | Moment | null | undefined,
  b: string | Date | Moment | null | undefined
): boolean => {
  const keyA = localDayKey(a);
  const keyB = localDayKey(b);
  return keyA !== null && keyB !== null && keyA === keyB;
};

/**
 * Midnight (local) of the Monday of the week containing `date`, the anchor of
 * a Monday-to-Sunday week view.
 */
export const startOfIsoWeek = (date: Date): Date => moment(date).startOf("isoWeek").toDate();

/**
 * `date` shifted by whole days (negative for earlier), keeping the local time
 * of day across DST changes.
 */
export const addDays = (date: Date, days: number): Date => moment(date).add(days, "days").toDate();

/**
 * The seven local days of the week starting at `weekStart` (Monday to Sunday
 * when `weekStart` comes from `startOfIsoWeek`).
 */
export const daysOfWeek = (weekStart: Date): Date[] =>
  Array.from({ length: 7 }, (_, index) => addDays(weekStart, index));
